/* eslint-disable max-len */
import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import StudentIntervention_Layout from 'src/components/FormLayouts/StudentIntervention/StudentIntervention_Layout';
import { StudentInterventionValidationEditSchema } from 'src/types/Validation/StudentInterventionValidationEditSchema';
import { StudentInterventionValidationSchema } from 'src/types/Validation/StudentInterventionValidationSchema';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import { useAppSelector } from '../../../../redux/hooks';
import {
  clearStudentIntervention,
  saveStudentIntervention,
  selectSchoolYear,
  selectStudentInterventionDataEntryMode,
  selectStudentInterventionFormViewModel,
  selectStudentInterventionLoadingFormViewModel,
  selectStudentInterventionLoadingStudentIntervention,
  selectStudentInterventionStudentIntervention,
  setStudentInterventionDataEntryMode,
} from '../../../../redux/slices/studentInterventionSlice';
import {
  fetchStudentInterventionById,
  fetchStudentInterventionFormViewModel,
} from '../../../../redux/thunks/studentInterventionThunks';
import { getCurrentUserAccess } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import {
  createStudentIntervention,
  updateStudentIntervention,
} from '../../../../services/studentIntervention.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { StudentInterventionFormViewModel } from '../../../../types/StudentInterventionFormViewModel';
import { StudentInterventionModel } from '../../../../types/StudentInterventionModel';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import { formatDistrictForDisplay } from '../../../../utilities/textUtilities';

const StudentInterventionInput = (): ReactElement => {
  const { schoolYear: urlSchoolYear } = useParams<{ schoolYear: string }>();
  const model: StudentInterventionModel = useAppSelector(
    selectStudentInterventionStudentIntervention
  );
  const formViewModel: StudentInterventionFormViewModel = useAppSelector(
    selectStudentInterventionFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectStudentInterventionLoadingFormViewModel
  );
  const isLoadingModel = useAppSelector(
    selectStudentInterventionLoadingStudentIntervention
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectStudentInterventionDataEntryMode
  );
  const reduxSchoolYear = useAppSelector(selectSchoolYear);
  const isLoading = isLoadingModel || isLoadingFormViewModel;

  const getDistrictDisplay = (): string => {
    if (dataEntryMode === DataEntryMode.EDIT) {
      return model.districtName;
    } else {
      const user = getCurrentUserAccess();
      return formatDistrictForDisplay(user?.currentDistrict);
    }
  };

  const getSchoolYear = (): string => {
    if (dataEntryMode == DataEntryMode.EDIT) {
      return model.schoolYear;
    } else {
      // Priority: URL parameter > Redux state > form model default
      if (urlSchoolYear && urlSchoolYear !== ':schoolYear') {
        return urlSchoolYear;
      } else if (reduxSchoolYear !== '') {
        return reduxSchoolYear;
      } else {
        return formViewModel.defaultSchoolYear;
      }
    }
  };

  const getSchoolDisplay = (): string => {
    return dataEntryMode === DataEntryMode.EDIT ? `${model.schoolName}` : '';
  };

  const intitalValues: StudentInterventionModel = {
    ...model,
    districtName: getDistrictDisplay(),
    schoolYear: getSchoolYear(),
    schoolName: getSchoolDisplay(),
  };

  return (
    <ManualInputPage<StudentInterventionModel, StudentInterventionFormViewModel>
      actions={ManualInputCrudAction.CREATE_AND_EDIT}
      loadingOptions={{
        isLoading: isLoading,
        loadingDataId: 'student-intervention-input-loader',
      }}
      navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
      classes={{
        pageClass: 'data-input--student-intervention--input',
      }}
      parameterOptions={{
        routeParameterName: 'studentInterventionId',
        identityName: model.id,
      }}
      routes={{
        backBarRoute: appRoutePaths.StudentInterventionList,
        cancelRoute: appRoutePaths.StudentInterventionList,
        createSuccessRoute: () =>
          appRoutePaths.StudentInterventionInput('new', getSchoolYear()),
      }}
      dataFunctions={{
        loadDataDetailById: fetchStudentInterventionById,
        loadFormDetail: fetchStudentInterventionFormViewModel,
        setEntryMode: setStudentInterventionDataEntryMode,
        setData: saveStudentIntervention,
        clearData: clearStudentIntervention,
        onCreate: createStudentIntervention,
        onUpdate: updateStudentIntervention,
      }}
      formDetails={{
        validationSchema:
          dataEntryMode === 'new'
            ? StudentInterventionValidationSchema
            : StudentInterventionValidationEditSchema,
        initialFormValues: intitalValues,
        isFormModelEmpty: true,
      }}
      config={{
        dataEntryMode: dataEntryMode,
        objectVerbiage: 'student intervention',
        pageHeader: 'Student',
      }}
      DEV_MODE={GLOBAL_DEBUG_MODE}
      REDIRECT_TO_LIST_PAGE={true}
    >
      <StudentIntervention_Layout />
    </ManualInputPage>
  );
};

export default StudentInterventionInput;
